/**
 * Cookie-based authentication for cross-domain, persistent login
 * 
 * This allows users to stay logged in:
 * - Across different websites (WordPress, direct links, emails)
 * - Across different 2WayMicropay subdomains (mail, products)
 * - After closing and reopening the browser
 * - For 24 hours unless they explicitly logout
 * 
 * The cookie is set on .2waymicropay.net domain so it works from:
 * - mail.2waymicropay.net (Mailbox)
 * - products.2waymicropay.net (Vendor Portal / Products)
 * - Any site that embeds these in an iframe
 */

interface AuthData {
  userId: string;
  username: string;
  token: string;
  loginTime: number; // Unix timestamp
  expiresAt: number; // Unix timestamp (24 hours from login)
}

const AUTH_COOKIE_BASE_NAME = 'qixit_auth';
const LEGACY_AUTH_COOKIE_NAME = AUTH_COOKIE_BASE_NAME;

type AuthEnvironment = 'dev' | 'stage' | 'prod';

function getAuthEnvironment(): AuthEnvironment {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return 'dev';
  }

  if (
    hostname.startsWith('st') ||
    hostname.includes('-stage') ||
    hostname.includes('.stage.')
  ) {
    return 'stage';
  }

  return 'prod';
}

// Cookie name for auth - MUST match products-web/src/services/cookieAuth.ts
const AUTH_COOKIE_NAME = `${AUTH_COOKIE_BASE_NAME}_${getAuthEnvironment()}`;

function getAuthCookieCandidates(): string[] {
  return Array.from(new Set([AUTH_COOKIE_NAME, LEGACY_AUTH_COOKIE_NAME]));
}

// Session duration: 24 hours in milliseconds
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Get the domain for cookie setting
 * For production: .2waymicropay.net (note leading dot for subdomain access)
 * For localhost: leave empty (uses current domain)
 */
function getCookieDomain(): string {
  const hostname = window.location.hostname;
  
  // For localhost development, don't set domain (browser default)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  
  // For production, use the root domain with leading dot
  if (hostname.includes('qixit.com')) {
    return '.qixit.com';
  }
  if (hostname.includes('2waymicropay.net')) {
    return '.2waymicropay.net';
  }
  if (hostname.includes('2waymicropay.com')) {
    return '.2waymicropay.com';
  }
  
  // For other domains, use current hostname
  return '';
}

/**
 * Set a cookie with proper cross-domain settings
 */
function setCookie(name: string, value: string, expiresMs: number): void {
  const expires = new Date(Date.now() + expiresMs).toUTCString();
  const domain = getCookieDomain();
  
  // Build cookie string
  let cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  
  // Add domain if specified
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  
  // Security settings for cross-site cookies
  // SameSite=None + Secure required for cross-site iframe access
  if (window.location.protocol === 'https:') {
    cookie += '; Secure; SameSite=None';
  } else {
    // For local dev over HTTP, use Lax
    cookie += '; SameSite=Lax';
  }
  
  document.cookie = cookie;
  console.log('🍪 Shared auth cookie set:', name, '(domain:', domain || 'default', ')');
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue.join('='));
    }
  }
  return null;
}

/**
 * Delete a cookie - must use same attributes as when it was set
 */
function deleteCookie(name: string): void {
  const domain = getCookieDomain();
  const isSecure = window.location.protocol === 'https:';
  
  // Build deletion cookie with same attributes used when setting
  let cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  if (isSecure) {
    cookie += '; Secure; SameSite=None';
  } else {
    cookie += '; SameSite=Lax';
  }
  document.cookie = cookie;
  
  // Also try without domain in case it was set that way
  let cookieNoDomain = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  if (isSecure) {
    cookieNoDomain += '; Secure; SameSite=None';
  }
  document.cookie = cookieNoDomain;
  
  // Also try without SameSite (for older browsers)
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

/**
 * Store auth credentials in shared cookie (persists for 24 hours, works cross-domain)
 * Call this after successful login to enable SSO across all 2WayMicropay sites
 */
export function setSharedAuthCookie(userId: string, username: string, token: string): void {
  const now = Date.now();
  const expiresAt = now + SESSION_DURATION_MS;
  
  const authData: AuthData = {
    userId,
    username,
    token,
    loginTime: now,
    expiresAt
  };
  
  setCookie(AUTH_COOKIE_NAME, JSON.stringify(authData), SESSION_DURATION_MS);
  if (AUTH_COOKIE_NAME !== LEGACY_AUTH_COOKIE_NAME) {
    deleteCookie(LEGACY_AUTH_COOKIE_NAME);
  }
  console.log('🔐 Shared auth cookie set for:', username, '(expires in 24 hours)');
}

/**
 * Get auth data from shared cookie (checks expiry)
 * Returns null if not authenticated or session expired
 */
export function getSharedAuthFromCookie(): AuthData | null {
  for (const cookieName of getAuthCookieCandidates()) {
    const cookieValue = getCookie(cookieName);
    if (!cookieValue) {
      continue;
    }

    try {
      const authData: AuthData = JSON.parse(cookieValue);
      
      // Check if expired
      if (authData.expiresAt) {
        if (Date.now() > authData.expiresAt) {
          console.log('🍪 Shared auth cookie expired (past 24 hours)');
          clearSharedAuthCookie();
          return null;
        }
      } else {
        // Legacy check for old cookies without expiresAt (24 hours from login)
        const age = Date.now() - authData.loginTime;
        if (age > SESSION_DURATION_MS) {
          console.log('🍪 Shared auth cookie expired (legacy 24hr check)');
          clearSharedAuthCookie();
          return null;
        }
      }

      if (cookieName === LEGACY_AUTH_COOKIE_NAME && AUTH_COOKIE_NAME !== LEGACY_AUTH_COOKIE_NAME) {
        setCookie(AUTH_COOKIE_NAME, JSON.stringify(authData), SESSION_DURATION_MS);
        deleteCookie(LEGACY_AUTH_COOKIE_NAME);
      }

      return authData;
    } catch (e) {
      console.error('Failed to parse shared auth cookie:', e);
      return null;
    }
  }

  return null;
}

/**
 * Clear shared auth cookie (call on logout)
 */
export function clearSharedAuthCookie(): void {
  for (const cookieName of getAuthCookieCandidates()) {
    deleteCookie(cookieName);
  }
  console.log('🔐 Shared auth cookie cleared');
}

/**
 * Check if user is authenticated via shared cookie
 */
export function isSharedAuthValid(): { authenticated: boolean; userId: string; username: string; token: string } {
  const authData = getSharedAuthFromCookie();
  
  if (authData) {
    return {
      authenticated: true,
      userId: authData.userId,
      username: authData.username,
      token: authData.token
    };
  }
  
  return {
    authenticated: false,
    userId: '',
    username: '',
    token: ''
  };
}
